import { Grid } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { FilterModel, SortModel } from '@tassat/common-definitions';
import { FormModal } from '@tassat/modal';
import React from 'react';

import Header from 'Containers/header';
import TassatPayLogo from 'Images/TassatLogoAndName.svg';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUser } from 'Selectors/common';
import PageHeaderTitle from 'SharedComponents/pageHeader/pageHeaderTitle';
import SideList, { GridModelContext } from 'SharedComponents/sideList';
import { IGridModelContext } from 'SharedComponents/sideList/gridModelContext';
import { GridToolbar } from 'SharedComponents/toolbar';

import { GridSettingsContext, ISideListLayoutProps } from '.';

export const useSideListLayoutStyles = makeStyles((theme: ApplicationMuiTheme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme?.app?.applicationColors.enumColors.offWhite ||
            '#f3f3f3'
    },
    pageHeaderContainer: {
        minHeight: '90px',
        flexBasis: '3.5rem',
        backgroundColor: theme?.app?.applicationColors.enumColors.white ||
            '#ffffff'
    },
    pageHeader: {
        display: 'inline-block',
        marginRight: '1rem',
        paddingLeft: '1.5rem'
    },
    filterBar: {
        flexBasis: 'auto',
        paddingTop: '.5rem',
        paddingBottom: '.25rem'
    },
    actionIcon: {
        '&:hover': {
            backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter ||
                '#cfcfcf'
        }
    },
    sideListContainer: {
        minWidth: '320px',
        position: 'relative' as const,
        backgroundColor: theme?.app?.applicationColors.enumColors.offWhite ||
            '#F3F3F3'
    },
    sideListGlobalHeaderColor: {
        backgroundColor: theme?.app?.applicationColors.enumColors.white ||
            '#FFFFFF'
    },
    sideList: {
        padding: '0 .75rem',
        marginBottom: '1.5rem'
    },
    sideListGlobalHeader: { padding: '0' },
    content: {
        position: 'relative' as const,
        backgroundColor: theme?.app?.applicationColors.backgroundAccented ||
            '#D7DBE4'
    },
    boxShadow: {
        position: 'absolute' as const,
        height: '100%',
        width: '100%',
        boxShadow: 'inset -0.8rem 0px 2rem -1rem black',
        zIndex: 10,
        pointerEvents: 'none' as const
    },
    tassatpayLogo: {
        height: '1.5rem',
        width: '7.5rem',
        marginLeft: '1rem',
        alignSelf: 'center' as const
    },
    switchControlIconRoot: { width: '10%' }
}));

export function SideListLayout(props: ISideListLayoutProps) {
    const {
        children,
        rowData,
        columnDefs,
        gridOptions,
        formOptions
    } = props;

    const user = useAppSelector(getUser) ?? {};
    const theme = useTheme<ApplicationMuiTheme>();
    const classes = useSideListLayoutStyles();

    const {
        useAppLogoOnSidelist,
        useGlobalHeader,
        useClientGlobalHeader
    } = theme?.app?.clientTheme.layoutSettings || {};

    const layoutSettings = React.useContext(GridSettingsContext);
    const [ sortModel, setSortModel ] = React.useState<SortModel | undefined>(undefined);
    const [ filterModel, setFilterModel ] = React.useState<FilterModel | undefined>(undefined);
    const [ search, setSearchValue ] = React.useState<string>('');
    const [ modalOpen, setModalOpen ] = React.useState<boolean>(false);
    const [ visibleRecords, setVisibleRecords ] = React.useState<Array<any>>([]);

    const isGlobalHeaderEnabled = React.useMemo(() => {
        return ((user.IsAdmin && useGlobalHeader) || (!user.IsAdmin && useClientGlobalHeader));
    }, [ user.IsAdmin, useGlobalHeader, useClientGlobalHeader ]);

    const sortData = React.useCallback((selectedOption) => {
        setSortModel(selectedOption.colId === 'clear' ? undefined : (prev) => {
            const sort = prev?.colId === selectedOption.colId && prev?.sort === 'asc' ? 'desc' : 'asc';

            return { colId: selectedOption.colId, sort };
        });
    }, []);

    const filterData = React.useCallback((selectedOption) => {
        setFilterModel(selectedOption?.colId === 'clear' ? undefined : selectedOption);
    }, []);

    const renderSideListTitle = () => {
        if (!isGlobalHeaderEnabled && useAppLogoOnSidelist && user && !user.IsAdmin) {
            return (
                <Grid
                    className={classes.pageHeaderContainer}
                    container
                    item
                    xs={12}
                >
                    <img
                        className={classes.tassatpayLogo}
                        src={TassatPayLogo}
                    />
                </Grid>
            );
        }

        if (layoutSettings?.pageTitle) {
            return (
                <Grid
                    className={classes.pageHeaderContainer}
                    container
                    item
                    xs={12}
                >
                    <PageHeaderTitle
                        form={Boolean(formOptions?.Form)}
                        onClick={renderModal}
                        title={layoutSettings.pageTitle}
                    />
                </Grid>
            );
        }

        return null;
    };

    const renderModal = () => {
        if (formOptions?.Form) setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const gridModel: IGridModelContext = React.useMemo(() => ({
        filterModel,
        onFilter: filterData,
        sortModel,
        onSort: sortData,
        onSearch: setSearchValue,
        searchValue: search,
        onFilterChange: setVisibleRecords
    }), [ filterModel, sortModel, search, filterData, sortData ]);

    return (
        <GridModelContext.Provider value={gridModel}>
            <Grid
                className={classes.root}
                container
                direction='row'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={`${classes.sideListContainer} ${isGlobalHeaderEnabled ? classes.sideListGlobalHeaderColor : ''}`}
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={3}
                >
                    {!isGlobalHeaderEnabled &&
                        <div className={classes.boxShadow} />}
                    {renderSideListTitle()}
                    <Grid
                        className={classes.filterBar}
                        item
                        xs={12}
                    >
                        <GridToolbar
                            gridOptions={gridOptions}
                            id='sideList'
                        />
                    </Grid>
                    <Grid
                        className={`${classes.sideList} ${isGlobalHeaderEnabled ? classes.sideListGlobalHeader : ''}`}
                        item
                        xs={12}
                    >
                        <SideList
                            columnDefs={columnDefs}
                            gridOptions={gridOptions}
                            rowData={rowData}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.content}
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={9}
                >
                    <Header />
                    {children}
                </Grid>
            </Grid>
            {formOptions?.Form &&
                <FormModal
                    Form={formOptions?.Form}
                    formProps={{
                    ...formOptions.formProps,
                    features: {
                        ...formOptions.formProps.features,
                        ...formOptions.formProps.features?.['authentication']
                    }
                }}
                    modalWidth={formOptions?.formModalWidth}
                // TODO: Make open and close of modal a reducer.
                    onClose={() => {
                    if (formOptions?.formProps?.onClose){
                        formOptions?.formProps.onClose();
                    }
                    closeModal();
                }}
                    open={modalOpen || formOptions?.formProps?.modalOpen || false}
                    title={formOptions?.formTitle}
                />}
        </GridModelContext.Provider>
    );
}